import React, { useCallback, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { fetch_payout_eligibility, fetch_payout_file_csv, fetch_payout_files } from '../api';
import {
  bank_file_name,
  eligibility_view,
  files_view,
  generate_choices,
  generate_month_options,
  over_limit_text,
  payout_error
} from './adapters';
import GenerateDialog from './generate_dialog';

/*
 * PROD-7828 W4 — the month's bank file: who the next file pays (with the large-amount, above-average and
 * retry flags), who it leaves out and why, the Generate / Regenerate / New round choices, and the month's files with
 * their download log. The bank file is downloaded as the server sealed it and never sent anywhere else.
 * props: pay_month, months (the page's month list, for the generate dialog), on_open_teacher(teacher_uuid), on_notify(type, message), on_error(e, fallback).
 */
const LOAD_FAILED_TEXT = 'Could not load this month’s bank file details.';
const DOWNLOAD_FAILED_TEXT = 'Could not download the bank file. Try again.';

const teacher_link = (on_open) => (r) => (
  <button
    type="button"
    className="tp-link"
    aria-label={`Open ${r.teacher_name}'s payouts`}
    onClick={() => on_open(r.teacher_uuid)}
  >
    {r.teacher_name}
  </button>
);

const eligible_columns = (on_open) => [
  { key: 'teacher', label: 'Teacher', hideInCard: true, render: teacher_link(on_open) },
  { key: 'amount', label: 'Amount', align: 'right' },
  { key: 'mode', label: 'Transfer' },
  {
    key: 'account',
    label: 'Account',
    render: (r) => (
      <div>
        <div className="tp-mono">{r.account_number_masked}</div>
        <div className="tp-hint">{r.bank_name}</div>
      </div>
    )
  },
  {
    key: 'flags',
    label: 'Check',
    render: (r) => (
      <div className="tp-po-pills">
        {r.flags.map((f, i) => (
          // eslint-disable-next-line react/no-array-index-key -- codes may repeat; the list is static
          <span key={`${f.code}-${i}`} title={f.text || undefined}>
            <StatusPill tone={f.tone}>{f.label}</StatusPill>
          </span>
        ))}
        {r.retry ? <StatusPill tone="blue">Paying again after a failed line</StatusPill> : null}
        {r.withheld ? (
          <StatusPill tone="orange">{`${r.withheld} withheld: duplicate payment recovery`}</StatusPill>
        ) : null}
      </div>
    )
  }
];

const left_out_columns = (on_open) => [
  { key: 'teacher', label: 'Teacher', hideInCard: true, render: teacher_link(on_open) },
  {
    key: 'reason_text',
    label: 'Why not in the file',
    render: (r) =>
      r.flagged ? (
        <div>
          <StatusPill tone={r.reason_view.tone}>{r.reason_view.label}</StatusPill>
          <div className="tp-hint">{r.reason_text}</div>
        </div>
      ) : (
        r.reason_text
      )
  },
  { key: 'outstanding', label: 'Still owed', align: 'right' }
];

const file_columns = (on_download, busy_uuid) => [
  { key: 'round', label: 'Round', render: (f) => `R${f.round_no}` },
  {
    key: 'file_name',
    label: 'File',
    hideInCard: true,
    render: (f) => (
      <div>
        <div className="tp-mono">{f.file_name}</div>
        <div className="tp-hint">
          Made by {f.generated_by} at {f.generated_at} IST · payment date {f.payment_date}
        </div>
      </div>
    )
  },
  {
    key: 'status',
    label: 'Status',
    render: (f) => (
      <div>
        <StatusPill tone={f.status_view.tone}>{f.status_view.label}</StatusPill>
        {f.cancel_reason ? <div className="tp-hint">{f.cancel_reason}</div> : null}
      </div>
    )
  },
  { key: 'rows', label: 'Lines', render: (f) => `${f.row_count}: ${f.counts_text || 'none'}` },
  { key: 'total', label: 'Total', align: 'right' },
  {
    key: 'downloads',
    label: 'Downloads',
    render: (f) =>
      f.downloads.length ? f.downloads.map((d) => `${d.by} at ${d.at}`).join('; ') : 'Never'
  },
  {
    key: 'download',
    label: 'Download',
    hideInCard: true,
    render: (f) => {
      const busy = busy_uuid === f.uuid;
      return (
        <button
          type="button"
          className="tp-link"
          aria-label={`Download bank file R${f.round_no}`}
          onClick={busy ? undefined : () => on_download(f)}
          aria-disabled={busy ? 'true' : undefined}
        >
          {busy ? 'Downloading…' : 'Download'}
        </button>
      );
    }
  }
];

const FilePanel = ({ pay_month, months, on_open_teacher, on_notify, on_error }) => {
  const [state, setState] = useState({ status: 'loading', elig: null, files: [], error: null });
  const [choice, setChoice] = useState(null);
  const [busy_uuid, setBusyUuid] = useState('');
  const [stale, setStale] = useState('');
  const seq = useRef(0);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    Promise.all([fetch_payout_eligibility(pay_month), fetch_payout_files(pay_month)])
      .then(([e, f]) => {
        if (mine !== seq.current) return;
        setState({ status: 'ready', elig: eligibility_view(e), files: files_view(f), error: null });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = payout_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        setState({ status: 'error', elig: null, files: [], error: v });
      });
  }, [pay_month, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const download = (file) => {
    setBusyUuid(file.uuid);
    fetch_payout_file_csv(file.uuid)
      .then(({ blob, file_name }) => {
        setBusyUuid('');
        saveAs(blob, bank_file_name(file, file_name));
        load(); // the download log is part of what Ops check before a regenerate
      })
      .catch((e) => {
        setBusyUuid('');
        const v = payout_error(e, DOWNLOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, DOWNLOAD_FAILED_TEXT);
        else on_notify('error', v.text);
      });
  };

  if (state.status === 'loading') return <CustomLoading />;
  if (state.status === 'error') {
    const k = state.error.kind;
    return k === 'forbidden' || k === 'before_cutover' ? (
      <div className="tp-banner tp-banner-info">{state.error.text}</div>
    ) : (
      <LoadFailed text={state.error.text} on_retry={load} />
    );
  }

  const { elig, files } = state;
  const choices = generate_choices(elig, files);
  const none_eligible = elig.eligible.length === 0;

  return (
    <div className="tp-po-file">
      {stale ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          {stale}{' '}
          <button
            type="button"
            className="tp-link"
            onClick={() => {
              setStale('');
              load();
            }}
          >
            Reload
          </button>
        </div>
      ) : null}

      <h3 className="tp-po-h3">
        Next file: {elig.eligible.length} {elig.eligible.length === 1 ? 'teacher' : 'teachers'},{' '}
        {elig.eligible_total}
      </h3>
      {elig.large_count ? (
        <div className="tp-banner tp-banner-warn">
          {elig.large_count} {elig.large_count === 1 ? 'line is' : 'lines are'} a large amount:
          check before generating.
        </div>
      ) : null}
      {elig.over_limit_count ? (
        <div className="tp-banner tp-banner-warn" role="status">
          {over_limit_text(elig.over_limit_count)}
        </div>
      ) : null}
      {elig.above_average_count ? (
        <div className="tp-banner tp-banner-warn">
          {elig.above_average_count} {elig.above_average_count === 1 ? 'line is' : 'lines are'} more
          than twice the teacher’s 3-month average: check before generating.
        </div>
      ) : null}
      <div className="tp-po-actions">
        {choices.map((c) => (
          <button
            key={c.key}
            type="button"
            className={c.kind === 'REGENERATE' ? 'tp-btn-out' : 'tp-btn'}
            title={
              c.disabled_text ||
              (none_eligible ? 'No teacher can be paid in this file.' : undefined)
            }
            aria-disabled={c.disabled_text || none_eligible ? 'true' : undefined}
            onClick={c.disabled_text || none_eligible ? undefined : () => setChoice(c)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {none_eligible ? (
        <div className="tp-hint">
          No teacher can be paid in a file right now: see who is left out below.
        </div>
      ) : null}
      <DataTable
        columns={eligible_columns(on_open_teacher)}
        rows={elig.eligible}
        rowKey={(r) => r.teacher_uuid}
        emptyText="Nobody to pay in the next file."
        cardTitle={(r) => <b>{r.teacher_name}</b>}
      />

      <h3 className="tp-po-h3">Left out ({elig.left_out.length})</h3>
      <DataTable
        columns={left_out_columns(on_open_teacher)}
        rows={elig.left_out}
        rowKey={(r) => r.teacher_uuid}
        rowClassName={(r) => (r.flagged ? 'tp-row-hl' : '')}
        emptyText="Nobody is left out."
        cardTitle={(r) => <b>{r.teacher_name}</b>}
      />

      <h3 className="tp-po-h3">Bank files for this month</h3>
      <DataTable
        columns={file_columns(download, busy_uuid)}
        rows={files}
        rowKey={(f) => f.uuid}
        emptyText="No bank file for this month yet."
        cardTitle={(f) => <b>{`R${f.round_no} · ${f.file_name}`}</b>}
      />

      {choice ? (
        <GenerateDialog
          pay_month={pay_month}
          month_options={generate_month_options(months, elig.cutover_month, pay_month)}
          choice={choice}
          eligible_count={elig.eligible.length}
          eligible_total={elig.eligible_total}
          on_close={() => setChoice(null)}
          on_done={(text) => {
            setChoice(null);
            on_notify('success', text);
            load();
          }}
          on_stale={(text) => {
            setChoice(null);
            setStale(text);
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default FilePanel;
